class TileCalculator extends HTMLElement {
  constructor() {
    super();

    try {
      this.packs = JSON.parse(this.dataset.packs || '[]')
        .filter((p) => p && p.variantId && p.tiles > 0)
        .sort((a, b) => a.tiles - b.tiles);
    } catch (e) {
      this.packs = [];
    }

    const tileSizeCm = parseFloat(this.dataset.tileSizeCm) || 33;
    this.tileAreaM2 = (tileSizeCm * tileSizeCm) / 10000;
    this.currency = this.dataset.currency || 'EUR';
    this.cartRedirect = this.dataset.cartRedirect === 'true';
    // Unit handling: 'm' (meters/m²) or 'ft' (feet/sq ft)
    this.unit = this.dataset.unit === 'ft' ? 'ft' : 'm';
    this.unitFactor = this.unit === 'ft' ? 0.3048 : 1.0; // ft → m
    this.unitArea = this.unit === 'ft' ? 'sq ft' : 'm²';

    this.inputs = {
      width: this.querySelector('[data-tc-input="width"]'),
      length: this.querySelector('[data-tc-input="length"]'),
      area: this.querySelector('[data-tc-input="area"]'),
      spare: this.querySelector('[data-tc-input="spare"]'),
    };

    this.result = this.querySelector('[data-tc-result]');
    this.tilesNeededEl = this.querySelector('[data-tc-tiles-needed]');
    this.areaNoteEl = this.querySelector('[data-tc-area-note]');
    this.visualEl = this.querySelector('[data-tc-visual]');
    this.visualCaptionEl = this.querySelector('[data-tc-visual-caption]');
    this.visualFrameEl = this.querySelector('[data-tc-visual-frame]');
    this.packsEl = this.querySelector('[data-tc-packs]');
    this.totalEl = this.querySelector('[data-tc-total]');
    this.perM2RowEl = this.querySelector('[data-tc-per-m2-row]');
    this.perM2El = this.querySelector('[data-tc-per-m2]');
    this.statusEl = this.querySelector('[data-tc-status]');
    this.addBtn = this.querySelector('[data-tc-action="add-all"]');

    this.lastCombo = null;

    if (!this.packs.length) {
      this.showStatus('No tile packs configured.', 'error');
      return;
    }

    this.attachListeners();
  }

  _safeSet(el, prop, val) { if (el) el[prop] = val; }

  attachListeners() {
    if (this.inputs.width) this.inputs.width.addEventListener('input', () => this.onDimensionInput());
    if (this.inputs.length) this.inputs.length.addEventListener('input', () => this.onDimensionInput());
    if (this.inputs.area) this.inputs.area.addEventListener('input', () => this.onAreaInput());
    if (this.inputs.spare) this.inputs.spare.addEventListener('change', () => this.recompute());
    if (this.addBtn) this.addBtn.addEventListener('click', () => this.addToCart());
  }

  onDimensionInput() {
    const w = this.parseNum(this.inputs.width);
    const l = this.parseNum(this.inputs.length);
    if (w > 0 && l > 0) {
      this.inputs.area.value = '';
    }
    this.recompute();
  }

  onAreaInput() {
    if (this.parseNum(this.inputs.area) > 0) {
      this.inputs.width.value = '';
      this.inputs.length.value = '';
    }
    this.recompute();
  }

  parseNum(input) {
    if (!input) return 0;
    const v = parseFloat(String(input.value).replace(',', '.'));
    return isFinite(v) && v > 0 ? v : 0;
  }

  computeAreaM2() {
    // Inputs in user's unit (m or ft) → return m² (the internal unit for tile math)
    const f = this.unitFactor;
    const area = this.parseNum(this.inputs.area);
    if (area > 0) return area * f * f;
    const w = this.parseNum(this.inputs.width);
    const l = this.parseNum(this.inputs.length);
    return (w * f) * (l * f);
  }

  displayArea(areaM2) {
    // Convert m² back to user's unit (m² or sq ft) for display
    return this.unit === 'ft' ? areaM2 * 10.7639 : areaM2;
  }

  recompute() {
    const area = this.computeAreaM2();
    if (area <= 0) {
      this.result.hidden = true;
      this.lastCombo = null;
      return;
    }

    const spare = this.inputs.spare && this.inputs.spare.checked;
    const factor = spare ? 1.10 : 1.0;
    // Area-based math: marketing-style "10-pack = 2 m²" model
    const tilesNeeded = Math.ceil(area * factor / this.tileAreaM2);

    const combo = this.cheapestCombo(tilesNeeded);
    if (!combo) {
      this.result.hidden = true;
      return;
    }

    this.lastCombo = combo;
    this.render(area, tilesNeeded, combo);
  }

  cheapestCombo(target) {
    if (target <= 0) return null;
    const packs = this.packs;
    const maxOvershoot = packs[packs.length - 1].tiles;
    const max = target + maxOvershoot;
    const dp = new Array(max + 1).fill(Infinity);
    const parent = new Array(max + 1).fill(-1);
    dp[0] = 0;
    for (let t = 1; t <= max; t++) {
      for (let i = 0; i < packs.length; i++) {
        const p = packs[i];
        if (t >= p.tiles && dp[t - p.tiles] + p.price < dp[t]) {
          dp[t] = dp[t - p.tiles] + p.price;
          parent[t] = i;
        }
      }
    }
    let bestT = -1;
    let bestCost = Infinity;
    for (let t = target; t <= max; t++) {
      if (dp[t] < bestCost) { bestCost = dp[t]; bestT = t; }
    }
    if (bestT < 0) return null;

    const counts = new Map();
    let t = bestT;
    while (t > 0 && parent[t] >= 0) {
      const i = parent[t];
      counts.set(i, (counts.get(i) || 0) + 1);
      t -= packs[i].tiles;
    }

    const items = [];
    counts.forEach((qty, i) => items.push({ pack: packs[i], qty }));
    items.sort((a, b) => b.pack.tiles - a.pack.tiles);

    return { items, totalTiles: bestT, totalPriceCents: bestCost };
  }

  render(areaM2, tilesNeeded, combo) {
    if (this.tilesNeededEl) this.tilesNeededEl.textContent = combo.totalTiles.toLocaleString();

    const areaText = `${this.formatNumber(this.displayArea(areaM2))} ${this.unitArea} · need ${tilesNeeded} tiles, supplied ${combo.totalTiles}`;
    if (this.areaNoteEl) this.areaNoteEl.textContent = areaText;

    this.renderVisual();

    if (this.packsEl) {
      this.packsEl.innerHTML = combo.items.map((item) => {
        const totalForLine = item.qty * item.pack.price;
        return `<li>
          <span>${item.qty}× ${this.escape(item.pack.title)} <span style="opacity:0.6">(${item.pack.tiles} tiles)</span></span>
          <strong>${this.formatMoney(totalForLine)}</strong>
        </li>`;
      }).join('');
    }

    if (this.totalEl) this.totalEl.textContent = this.formatMoney(combo.totalPriceCents);

    if (areaM2 > 0 && this.perM2El && this.perM2RowEl) {
      const perM2 = combo.totalPriceCents / this.displayArea(areaM2);
      this.perM2El.textContent = this.formatMoney(perM2);
      this.perM2RowEl.hidden = false;
    } else if (this.perM2RowEl) {
      this.perM2RowEl.hidden = true;
    }

    if (this.statusEl) this.statusEl.hidden = true;
    if (this.result) this.result.hidden = false;
  }

  renderVisual() {
    if (!this.visualEl) return;
    if (!this.lastCombo) { this.visualEl.hidden = true; return; }

    const totalTiles = this.lastCombo.totalTiles;
    if (totalTiles <= 0) { this.visualEl.hidden = true; return; }

    const w = this.parseNum(this.inputs.width);
    const l = this.parseNum(this.inputs.length);
    const area = this.parseNum(this.inputs.area);

    // Compute grid from tile count + aspect ratio (so visual matches the count)
    let cols, rows, isApprox;
    const aspect = (w > 0 && l > 0) ? (w / l) : 1;
    cols = Math.max(1, Math.round(Math.sqrt(totalTiles * aspect)));
    rows = Math.max(1, Math.ceil(totalTiles / cols));
    isApprox = !(w > 0 && l > 0);

    const MAX_TILES_RENDERED = 4000;
    let displayCols = cols;
    let displayRows = rows;
    let displayTotal = totalTiles;
    let scaled = false;
    if (totalTiles > MAX_TILES_RENDERED) {
      const ratio = Math.sqrt(MAX_TILES_RENDERED / (cols * rows));
      displayCols = Math.max(2, Math.floor(cols * ratio));
      displayRows = Math.max(2, Math.floor(rows * ratio));
      displayTotal = Math.min(displayCols * displayRows, MAX_TILES_RENDERED);
      scaled = true;
    }

    const TILE_PX = 14;
    const GAP = 1;
    const svgW = displayCols * TILE_PX;
    const svgH = displayRows * TILE_PX;

    const parts = [];
    let drawn = 0;
    for (let r = 0; r < displayRows; r++) {
      for (let c = 0; c < displayCols; c++) {
        if (drawn >= displayTotal) break;
        const x = c * TILE_PX;
        const y = r * TILE_PX;
        const altClass = (r + c) % 2 === 0 ? '' : ' tc-tile--alt';
        parts.push(`<rect class="tc-tile${altClass}" x="${x + GAP / 2}" y="${y + GAP / 2}" width="${TILE_PX - GAP}" height="${TILE_PX - GAP}" rx="1" />`);
        drawn++;
      }
    }

    const svg = `<svg viewBox="0 0 ${svgW} ${svgH}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Tile layout preview">${parts.join('')}</svg>`;

    let caption;
    if (isApprox) {
      caption = `Approximate layout: ${totalTiles} tiles (your space shape may vary).`;
    } else {
      caption = `${totalTiles} tiles arranged ~${cols} × ${rows} for ${this.formatNumber(w)} × ${this.formatNumber(l)} ${this.unit}.`;
    }
    if (scaled) {
      caption += ` Preview scaled to fit.`;
    }

    this.visualCaptionEl.textContent = caption;
    this.visualFrameEl.innerHTML = svg;
    this.visualEl.hidden = false;
  }

  async addToCart() {
    if (!this.lastCombo) return;
    this.dataset.loading = 'true';
    this.statusEl.hidden = true;

    const items = this.lastCombo.items.map((item) => ({
      id: item.pack.variantId,
      quantity: item.qty,
    }));

    try {
      const res = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      this.afterAdd();
    } catch (e) {
      this.showStatus(e.message || 'Could not add to cart. Please try again.', 'error');
    } finally {
      this.dataset.loading = 'false';
    }
  }

  afterAdd() {
    this.showStatus('Added to cart — taking you there…', 'success');
    if (this.cartRedirect) {
      setTimeout(() => {
        window.location.href = window.theme?.routes?.cart || '/cart';
      }, 400);
    }
  }

  showStatus(msg, kind) {
    if (!this.statusEl) return;
    this.statusEl.textContent = msg;
    this.statusEl.classList.remove('tile-calculator__status--error', 'tile-calculator__status--success');
    if (kind === 'error') this.statusEl.classList.add('tile-calculator__status--error');
    if (kind === 'success') this.statusEl.classList.add('tile-calculator__status--success');
    this.statusEl.hidden = false;
  }

  formatMoney(cents) {
    const value = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch (e) {
      return `${this.currency} ${value.toFixed(2)}`;
    }
  }

  formatNumber(v) {
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

customElements.define('tile-calculator', TileCalculator);
